import moment from "moment";
import React from "react";
import { useRillContext } from "../../../../../rill/RillReact/share/base";
import {
  getStartingCalendarDayAdjustment,
  getWeekColumnData,
  getWeeklyStatsAvg,
  isCurrentWeek,
} from "../../../../../utils/annual-planner-helpers";
import { parseTime } from "../../../calendar/sleep-time/SleepTime";

// Stats and volumes carry utcDate as a zero padded YYYYMMDD, so range checks are plain
// integer comparisons. Building a moment per row was by far the hottest cost in this view:
// every week of every month rescans the loaded stats, which is a full year of data
const toUtcDate = (momentDate) => Number(momentDate.format("YYYYMMDD"));

const isInRange = (utcDate, from, to) => {
  const date = Number(utcDate);
  return date >= from && date <= to;
};

export const StatsCard = ({
  month,
  startingCalendarDay,
  numberOfWeeks,
  athleteStatsState,
  athleteVolumesState,
  isAnnualPlanner = true,
  // Periodization's Calendar view only (see CalendarView.js's own call) - the
  // "Weekly Volume" row label already says "(Kg)" there, so formatVolume's
  // own " kg"/" lb" suffix on each value would just repeat it. Every other
  // caller of this component (MonthColumn.js) keeps the suffix as-is.
  hideVolumeUnit = false,
  hasWideStatValue = false,
}) => {
  const { locals: activeUser } = useRillContext();

  const { units } = activeUser;

  const { monthFirstDay, monthLastDay } = getMonthRange(
    moment(month),
    startingCalendarDay
  );

  const monthFrom = toUtcDate(monthFirstDay);
  const monthTo = toUtcDate(monthLastDay);

  // Getting the athlete volumes corresponding to the current month
  const monthVolumes =
    athleteVolumesState?.filter((volume) =>
      isInRange(volume.utcDate, monthFrom, monthTo)
    ) || [];

  // Getting the athlete stats corresponding to the current month
  const monthStats =
    athleteStatsState?.filter((stat) =>
      isInRange(stat.utcDate, monthFrom, monthTo)
    ) || [];

  const hiddenStats = moment.utc().isBefore(moment(monthFirstDay));

  return (
    <div className={`stats-card`}>
      {/* We map a column for each week, containing the weekly summary for the athlete stats */}
      {Array.from(Array(numberOfWeeks).keys(), (weekNumber) => {
        const weekFirstDay = getWeekColumnData(
          month,
          startingCalendarDay,
          weekNumber
        );
        const weekLastDay = moment(weekFirstDay).add(6, "days");

        const weekFrom = toUtcDate(weekFirstDay);
        const weekTo = toUtcDate(weekLastDay);
        const prevWeekFrom = toUtcDate(
          moment(weekFirstDay).subtract(7, "days")
        );
        const prevWeekTo = toUtcDate(moment(weekLastDay).subtract(7, "days"));

        // Filtering the current week volumnes from the month array
        const currentWeekVolumes = monthVolumes.filter((volume) =>
          isInRange(volume.utcDate, weekFrom, weekTo)
        );

        // Filtering the current week stats from the month array
        const currentWeekStats = monthStats.filter((stat) =>
          isInRange(stat.utcDate, weekFrom, weekTo)
        );

        // The week before the first week of a month falls in the previous month, so this
        // has to look at the whole loaded range and not at monthStats
        const previousWeekStats = athleteStatsState.filter((stat) =>
          isInRange(stat.utcDate, prevWeekFrom, prevWeekTo)
        );

        return (
          <StatsColumn
            key={weekFirstDay.format("YYYYMMDD")}
            weekFirstDay={weekFirstDay}
            currentWeekVolumes={currentWeekVolumes}
            currentWeekStats={currentWeekStats}
            hiddenStats={hiddenStats}
            isAnnualPlanner={isAnnualPlanner}
            previousWeekStats={previousWeekStats}
            units={units}
            hideVolumeUnit={hideVolumeUnit}
            hasWideStatValue={hasWideStatValue}
          />
        );
      })}
    </div>
  );
};

const StatsColumn = ({
  weekFirstDay,
  currentWeekStats,
  currentWeekVolumes,
  hiddenStats,
  isAnnualPlanner,
  previousWeekStats,
  units = "metric",
  hideVolumeUnit = false,
  hasWideStatValue = false,
}) => {
  // Getting the weekly averages or sums to each stat
  const {
    volumes,
    readinessAvg,
    sessionRPEAvg,
    bodyWeightAvg,
    stepsAvg,
    sleepAvg,
    bodyWeightDiff,
  } = getWeeklyStatsAvg(
    currentWeekStats,
    currentWeekVolumes,
    previousWeekStats,
    units
  );

  // Periodization's Tracked Results is scoped to just these 4 signals (PRD
  // 7.6) - kCal/Steps dropped, they're "Out" there (already represented
  // elsewhere: nutrition progress page, daily targets). Weeks with no data
  // render an empty cell (nutrition-plan-calendar's own stats below still
  // fall back to "-").
  const currentWeekClass = isCurrentWeek(weekFirstDay) ? " current-week" : "";

  // formatVolume (getWeeklyStatsAvg) appends " kg"/" lb" to every value - the
  // row label already carries that unit here (see StatsCard's hideVolumeUnit
  // comment), so strip the trailing word instead of showing it twice.
  const displayVolume = hideVolumeUnit
    ? volumes?.replace(/ (kg|lb)$/, "")
    : volumes;

  // Tracked Results only (isAnnualPlanner) - the other branch below is a
  // different page (nutrition-plan-calendar's own MonthColumn.js), not
  // Tracked Results, so it never shrinks.
  const wideValueClass = hasWideStatValue ? " compact-values" : "";

  return isAnnualPlanner ? (
    <div
      className={`stats-column${hiddenStats ? " hidden" : ""}${currentWeekClass}${wideValueClass}`}
    >
      <span>{readinessAvg || ""}</span>
      <span>{sessionRPEAvg || ""}</span>
      <span>{displayVolume || ""}</span>
      <span>{bodyWeightAvg || ""}</span>
    </div>
  ) : (
    <div
      className={`stats-column${hiddenStats ? " hidden" : ""}${currentWeekClass}`}
    >
      <span>{bodyWeightAvg || "-"}</span>
      <span>{bodyWeightDiff || "-"}</span>
      <span>{stepsAvg || "-"}</span>
      <span>{sleepAvg ? parseTime(sleepAvg, "hours") : "-"}</span>
    </div>
  );
};

export const getMonthRange = (month, startingCalendarDay) => {
  // Getting the first day of the month, and the days amount necessary for the starting calendar day adjustment
  const monthFirstDay = moment(month).startOf("month");
  // The amount of days that we have to sum/subtract to adjust to the starting calendar day
  const startAmout = getStartingCalendarDayAdjustment(
    monthFirstDay,
    startingCalendarDay
  );

  monthFirstDay.startOf("week").add(startAmout, "days");

  // Getting the last day of the month, and the days amount necessary for the starting calendar day adjustment
  const monthLastDay = moment(month).endOf("month");
  // The amount of days that we have to sum/subtract to adjust to the starting calendar day
  const endAmount = getStartingCalendarDayAdjustment(
    monthLastDay,
    startingCalendarDay
  );

  monthLastDay.endOf("week").add(endAmount, "days");

  // Ensure that we are not taking into account the first week of the next month if it includes current month days
  monthLastDay.month() !== moment(month).month() &&
    monthLastDay.subtract(1, "week");

  return { monthFirstDay, monthLastDay };
};

//    [    D,    L,    M,    Mie,    J,    V,    S     ]
//
//         |     º
//    [    D,    L,    M,    Mie,    J,    V,    S     ]
//
//
//    [    D,    L,    M,    Mie,    J,    V,    S     ]
//
